/** Study subject helpers — courses come from the catalog API, not a hardcoded list. */
import { resolveImageUrl } from "./api";
import studentCopy from "@shared/student_copy.json";

export type StudySubject = {
  id: string;
  code: string;
  courseName: string;
  /** Filter key used by modules/exams/quiz APIs (e.g. "Math"). */
  subject: string;
  slug: string;
  /** Display name on the picker tile (course name). */
  name: string;
  emoji?: string;
  color?: string;
  resourceCount?: number;
  imageUrl?: string | null;
  semester?: number;
  streamId?: string | null;

  streamCode?: string | null;
  streamName?: string | null;
  stream?: "common" | "natural" | "social" | string;
  counts?: {
    modules?: number;
    quiz?: number;
    exams?: number;
    notes?: number;
  };
};

export type StudyActivity = "modules" | "quiz" | "exams" | "notes";

export const STUDY_ACTIVITIES: {
  id: StudyActivity;
  title: string;
  description: string;
  actionText: string;
  icon: "book" | "brain" | "trophy" | "sparkles";
}[] = [
    { id: "modules", title: studentCopy.cards.modules.title, description: studentCopy.cards.modules.description, actionText: studentCopy.cards.modules.title, icon: "book" },
    { id: "quiz", title: studentCopy.cards.quizzes.title, description: studentCopy.cards.quizzes.description, actionText: studentCopy.cards.quizzes.title, icon: "brain" },
    { id: "exams", title: studentCopy.cards.pastExams.title, description: studentCopy.cards.pastExams.description, actionText: studentCopy.cards.pastExams.title, icon: "trophy" },
    { id: "notes", title: studentCopy.cards.shortNotes.title, description: studentCopy.cards.shortNotes.description, actionText: studentCopy.cards.shortNotes.title, icon: "sparkles" },
  ];

const VISUAL_PALETTE: { emoji: string; color: string }[] = [
  { emoji: "📐", color: "from-orange-500 to-amber-400" },
  { emoji: "⚛️", color: "from-blue-500 to-cyan-400" },
  { emoji: "🧪", color: "from-emerald-500 to-teal-400" },
  { emoji: "🧬", color: "from-green-500 to-lime-400" },
  { emoji: "📖", color: "from-violet-500 to-purple-400" },
  { emoji: "🏛️", color: "from-rose-500 to-pink-400" },
  { emoji: "🏃", color: "from-sky-500 to-indigo-400" },
  { emoji: "💡", color: "from-amber-500 to-yellow-400" },
];

const KNOWN_VISUALS: Record<string, { emoji: string; color: string }> = {
  math: { emoji: "📐", color: "from-orange-500 to-amber-400" },
  mathematics: { emoji: "📐", color: "from-orange-500 to-amber-400" },
  physics: { emoji: "⚛️", color: "from-blue-500 to-cyan-400" },
  chemistry: { emoji: "🧪", color: "from-emerald-500 to-teal-400" },
  biology: { emoji: "🧬", color: "from-green-500 to-lime-400" },
  english: { emoji: "📖", color: "from-violet-500 to-purple-400" },
  civics: { emoji: "🏛️", color: "from-rose-500 to-pink-400" },
  sport: { emoji: "🏃", color: "from-sky-500 to-indigo-400" },
  "sport-science": { emoji: "🏃", color: "from-sky-500 to-indigo-400" },
};

export function slugifySubject(value: string): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "general";
}

function hashPick(seed: string): { emoji: string; color: string } {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return VISUAL_PALETTE[Math.abs(h) % VISUAL_PALETTE.length]!;
}

export function withSubjectVisuals(item: StudySubject): StudySubject {
  const key = slugifySubject(item.subject);
  const known = KNOWN_VISUALS[key] || KNOWN_VISUALS[slugifySubject(item.courseName)] || hashPick(item.code || item.name);
  return {
    ...item,
    imageUrl: resolveImageUrl(item.imageUrl),
    emoji: known.emoji,
    color: known.color,
  };
}

/** Resolve a URL slug against a loaded catalog list (code slug or legacy subject slug). */
export function findStudySubject(subjects: StudySubject[], slug: string): StudySubject | null {
  const needle = slugifySubject(slug);
  // Return the catalog object by reference — callers memoize on it; do not clone every lookup.
  return (
    subjects.find((s) => s.slug === needle) ||
    subjects.find((s) => slugifySubject(s.subject) === needle) ||
    subjects.find((s) => slugifySubject(s.name) === needle) ||
    null
  );
}

/** Best-effort slug for redirects when only a subject label is known. */
export function slugFromSubject(name: string, subjects?: StudySubject[]): string {
  if (subjects?.length) {
    const found =
      subjects.find((s) => s.subject.toLowerCase() === name.toLowerCase()) ||
      subjects.find((s) => s.name.toLowerCase() === name.toLowerCase());
    if (found) return found.slug;
  }
  return slugifySubject(name);
}

/** @deprecated Prefer findStudySubject with API data — kept for simple label checks. */
export function subjectFromSlug(slug: string): string | null {
  return slug ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null;
}

export function subjectMeta(slug: string, subjects?: StudySubject[]): StudySubject | null {
  if (subjects?.length) return findStudySubject(subjects, slug);
  const name = subjectFromSlug(slug);
  if (!name) return null;
  return withSubjectVisuals({
    id: "",
    code: slug.toUpperCase(),
    courseName: name,
    subject: name,
    slug: slugifySubject(slug),
    name,
  });
}
